/**
 *
 * ████████╗ ██████╗ ██╗   ██╗██████╗  ██████╗ ██╗   ██╗██╗██████╗ ███████╗         ██╗███████╗
 * ╚══██╔══╝██╔═══██╗██║   ██║██╔══██╗██╔════╝ ██║   ██║██║██╔══██╗██╔════╝         ██║██╔════╝
 *    ██║   ██║   ██║██║   ██║██████╔╝██║  ███╗██║   ██║██║██║  ██║█████╗           ██║███████╗
 *    ██║   ██║   ██║██║   ██║██╔══██╗██║   ██║██║   ██║██║██║  ██║██╔══╝      ██   ██║╚════██║
 *    ██║   ╚██████╔╝╚██████╔╝██║  ██║╚██████╔╝╚██████╔╝██║██████╔╝███████╗    ╚█████╔╝███████║
 *    ╚═╝    ╚═════╝  ╚═════╝ ╚═╝  ╚═╝ ╚═════╝  ╚═════╝ ╚═╝╚═════╝ ╚══════╝     ╚════╝ ╚══════╝
 *
 */
import { createTourGuideDialog } from './core/dialog'
import computeTourPositions from './core/positioning'
import {
  computeBackdropAttributes,
  createTourGuideBackdrop,
} from './core/backdrop'
import {
  handleOnAfterExit,
  handleOnAfterStepChange,
  handleOnBeforeExit,
  handleOnBeforeStepChange,
  handleOnFinish,
} from './core/callbacks'
import {
  clickOutsideHandler,
  handleDestroyListeners,
  handleInitListeners,
  keyPressHandler,
} from './core/listeners'
// HANDLERS
import handleVisitStep, {
  handleVisitNextStep,
  handleVisitPrevStep,
} from './handlers/handleVisitStep'
import handleAddStep from './handlers/handleAddStep'
import handleTourStart from './handlers/handleTourStart'
import handleSetOptions from './handlers/handleSetOptions'
import handleClose from './handlers/handleClose'
import handleRefreshTour, {
  handleRefreshDialog,
} from './handlers/handleRefresh'
import handleFinishTour, {
  delFinishedTour,
  getIsFinished,
} from './handlers/handleFinishTour'
// UTIL
import defaultOptions from './util/util_default_options'
// Tour
class TourGuideClient {
  /**
   * Constructor
   * @param options
   */
  constructor(options) {
    /**
     * Default Attributes
     */
    this.group = ''
    this.isVisible = false
    this.activeStep = 0
    this.tourSteps = []
    this.options = defaultOptions
    this.isFinished = getIsFinished
    /**
     * Backdrop / Target highlighter
     */
    this.createTourGuideBackdrop = createTourGuideBackdrop
    this.computeBackdropAttributes = computeBackdropAttributes
    /**
     * Dialog
     */
    this.createTourGuideDialog = createTourGuideDialog
    /**
     * Methods
     */
    this.start = handleTourStart // Start the tour - compute steps -> goToStep (checks -> update dialog html, dialog & backdrop) -> initListeners()
    this.visitStep = handleVisitStep // visit step by stepIndex or `next` | `prev`
    this.addSteps = handleAddStep // Push new steps to the tour
    this.nextStep = handleVisitNextStep // navigate to next step - also handles calling finishTour() on final step
    this.prevStep = handleVisitPrevStep // navigate to previous step
    this.exit = handleClose // exit the tour
    this.refresh = handleRefreshTour // Recompute everything including tour guide steps
    this.refreshDialog = handleRefreshDialog // Recompute the dialog content & backdrop only
    this.finishTour = handleFinishTour // Set tour as complete in localStorage & exit - pass group key
    this.updatePositions = computeTourPositions // Set tour as complete in localStorage & exit - pass group key
    this.deleteFinishedTour = delFinishedTour // Remove a completed tour from localStorage. Pass group key or `all` to clear.
    this.setOptions = handleSetOptions // Update tour options & refresh dialog + backdrop
    /**
     * Listeners
     */
    // Init
    this.initListeners = handleInitListeners
    // Destroy
    this.destroyListeners = handleDestroyListeners
    // Track initialised eventListeners
    this._trackedEvents = {
      nextBtnClickEvent: {
        initialized: false,
        fn: this.nextStep.bind(this),
      },
      prevBtnClickEvent: {
        initialized: false,
        fn: this.prevStep.bind(this),
      },
      closeBtnClickEvent: {
        initialized: false,
        fn: this.exit.bind(this),
      },
      keyPressEvent: {
        initialized: false,
        fn: keyPressHandler.bind(this),
      },
      outsideClickEvent: {
        initialized: false,
        fn: clickOutsideHandler.bind(this),
      },
    }
    // FINISH
    this.onFinish = handleOnFinish
    // EXIT
    this.onBeforeExit = handleOnBeforeExit
    this.onAfterExit = handleOnAfterExit
    // STEP CHANGE
    this.onBeforeStepChange = handleOnBeforeStepChange
    this.onAfterStepChange = handleOnAfterStepChange
    this.dialog = document.createElement('div')
    this.backdrop = document.createElement('div')
    this.options = defaultOptions
    if (options) Object.assign(this.options, options) // overwrite default options
    // if(steps) this.options.steps = steps // defined steps array
    this.createTourGuideDialog().catch(e => {
      if (this.options.debug) console.warn(e)
    })
    this.createTourGuideBackdrop()
  }
}
// Fn wrapper for script instantiation
// *** Redundant ****
// const createInstance = (options? : TourGuideOptions)=>{
//     return new TourGuideClient(options)
// }
//
// export default createInstance
export {
  // Client
  TourGuideClient,
}
